import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type EditableRow = {
  clientId: string;
  id: number;
};

type RowTransform<T extends EditableRow> = (rows: T[], row: T) => T[];

// Which fields of a row the user has edited, and which of those are touched:
// their value was committed (focus left the field, or it was picked in a
// select or a date picker).
type RowFieldState = {
  edited: ReadonlySet<string>;
  touched: ReadonlySet<string>;
};

// Decides which errors of a new row are shown; see getLiveRowErrors.
export type RowErrorVisibility = {
  saveAttemptedRowIds: ReadonlySet<string>;
  touchedFields: ReadonlyMap<string, ReadonlySet<string>>;
};

function getChangedFields<T extends EditableRow>(previous: T, next: T) {
  return Object.keys(next).filter(
    (key) => !Object.is(previous[key as keyof T], next[key as keyof T]),
  );
}

type EditableRowListOptions<T extends EditableRow> = {
  areRowsEqual: (first: T[], second: T[]) => boolean;
  createEmptyRow: (temporaryId: number, isInitial: boolean) => T;
  defaultEditing?: boolean;
  isLoading?: boolean;
  mergeServerRowsWhileEditing?: boolean;
  serverRows: T[];
};

type EditableRowListState<T extends EditableRow, E> = {
  addRow: (transform?: RowTransform<T>) => T;
  // Stores the errors found by a save attempt. From then on the validated
  // rows show all their errors, not only those of touched fields.
  applySaveValidation: (errors: Map<string, E>) => void;
  cancelEditing: () => void;
  changeRow: (row: T, transform?: RowTransform<T>) => void;
  clearValidationErrors: () => void;
  completeSave: () => void;
  deleteRow: (row: T, transform?: RowTransform<T>) => void;
  deletedRows: T[];
  editingRowIds: Set<string>;
  enterEditing: () => void;
  hasChanges: boolean;
  isEditing: boolean;
  openRows: (clientIds: Iterable<string>) => void;
  patchRow: (clientId: string, changes: Partial<T>) => void;
  rowErrorVisibility: RowErrorVisibility;
  rows: T[];
  setEditingRowIds: Dispatch<SetStateAction<Set<string>>>;
  setRows: Dispatch<SetStateAction<T[]>>;
  // Marks a field as committed; it counts only if the user has edited it.
  touchField: (clientId: string, field: string) => void;
  validationErrors: Map<string, E>;
  viewModeSignal: number | undefined;
};

export function useEditableRowList<T extends EditableRow, E>({
  areRowsEqual,
  createEmptyRow,
  defaultEditing = false,
  isLoading = false,
  mergeServerRowsWhileEditing = false,
  serverRows,
}: EditableRowListOptions<T>): EditableRowListState<T, E> {
  const [rows, setRows] = useState<T[]>([]);
  const [editingRowIds, setEditingRowIds] = useState<Set<string>>(new Set());
  const [isEditing, setIsEditing] = useState(defaultEditing);
  const [deletedRowIds, setDeletedRowIds] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<Map<string, E>>(
    new Map(),
  );
  const [fieldStates, setFieldStates] = useState<Map<string, RowFieldState>>(
    new Map(),
  );
  const [saveAttemptedRowIds, setSaveAttemptedRowIds] = useState<Set<string>>(
    new Set(),
  );
  const [viewModeSignal, setViewModeSignal] = useState<number | undefined>();
  const nextTemporaryId = useRef(-1);
  // Lets changeRow compare a row with its previous state.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const markFieldsEdited = useCallback(
    (clientId: string, fields: readonly string[]) => {
      if (fields.length === 0) {
        return;
      }
      setFieldStates((current) => {
        const state = current.get(clientId);
        if (state && fields.every((field) => state.edited.has(field))) {
          return current;
        }
        return new Map(current).set(clientId, {
          edited: new Set([...(state?.edited ?? []), ...fields]),
          touched: state?.touched ?? new Set(),
        });
      });
    },
    [],
  );

  const touchField = useCallback((clientId: string, field: string) => {
    setFieldStates((current) => {
      const state = current.get(clientId);
      if (!state?.edited.has(field) || state.touched.has(field)) {
        return current;
      }
      return new Map(current).set(clientId, {
        edited: state.edited,
        touched: new Set(state.touched).add(field),
      });
    });
  }, []);

  const applySaveValidation = useCallback((errors: Map<string, E>) => {
    setValidationErrors(errors);
    setSaveAttemptedRowIds(
      (current) => new Set([...current, ...errors.keys()]),
    );
  }, []);

  const initializeEditing = useCallback(() => {
    const initialRow = createEmptyRow(-1, true);
    nextTemporaryId.current = -2;
    setRows([initialRow, ...serverRows.map((row) => ({ ...row }))]);
    setEditingRowIds(new Set([initialRow.clientId]));
    setDeletedRowIds(new Set());
    setValidationErrors(new Map());
    setFieldStates(new Map());
    setSaveAttemptedRowIds(new Set());
  }, [createEmptyRow, serverRows]);

  useEffect(() => {
    if (defaultEditing && isEditing && !isLoading && rows.length === 0) {
      initializeEditing();
    }
  }, [defaultEditing, initializeEditing, isEditing, isLoading, rows.length]);

  useEffect(() => {
    if (!mergeServerRowsWhileEditing || !isEditing) {
      return;
    }
    setRows((current) => {
      const currentIds = new Set(current.map((row) => row.clientId));
      const additions = serverRows.filter(
        (row) =>
          !currentIds.has(row.clientId) && !deletedRowIds.has(row.clientId),
      );
      return additions.length ? [...current, ...additions] : current;
    });
  }, [deletedRowIds, isEditing, mergeServerRowsWhileEditing, serverRows]);

  const reset = useCallback(() => {
    nextTemporaryId.current = -1;
    setRows([]);
    setEditingRowIds(new Set());
    setDeletedRowIds(new Set());
    setValidationErrors(new Map());
    setFieldStates(new Map());
    setSaveAttemptedRowIds(new Set());
    setIsEditing(false);
  }, []);

  const enterEditing = useCallback(() => {
    setIsEditing(true);
    initializeEditing();
  }, [initializeEditing]);

  const addRow = useCallback(
    (transform?: RowTransform<T>) => {
      const row = createEmptyRow(nextTemporaryId.current, false);
      nextTemporaryId.current -= 1;
      setRows((current) =>
        transform ? transform(current, row) : [row, ...current],
      );
      setEditingRowIds((current) => new Set(current).add(row.clientId));
      setValidationErrors(new Map());
      return row;
    },
    [createEmptyRow],
  );

  const changeRow = useCallback(
    (row: T, transform?: RowTransform<T>) => {
      const previous = rowsRef.current.find(
        (item) => item.clientId === row.clientId,
      );
      if (previous) {
        markFieldsEdited(row.clientId, getChangedFields(previous, row));
      }
      setRows((current) =>
        transform
          ? transform(current, row)
          : current.map((item) =>
              item.clientId === row.clientId ? row : item,
            ),
      );
      setValidationErrors(new Map());
    },
    [markFieldsEdited],
  );

  const deleteRow = useCallback((row: T, transform?: RowTransform<T>) => {
    if (row.id > 0) {
      setDeletedRowIds((current) => new Set(current).add(row.clientId));
    }
    setRows((current) =>
      transform
        ? transform(current, row)
        : current.filter((item) => item.clientId !== row.clientId),
    );
    setEditingRowIds((current) => {
      const next = new Set(current);
      next.delete(row.clientId);
      return next;
    });
    setValidationErrors(new Map());
  }, []);

  const patchRow = useCallback(
    (clientId: string, changes: Partial<T>) => {
      markFieldsEdited(clientId, Object.keys(changes));
      setRows((current) =>
        current.map((row) =>
          row.clientId === clientId ? { ...row, ...changes } : row,
        ),
      );
      setValidationErrors(new Map());
    },
    [markFieldsEdited],
  );

  const openRows = useCallback((clientIds: Iterable<string>) => {
    setEditingRowIds(
      (current) => new Set([...current, ...Array.from(clientIds)]),
    );
  }, []);

  const completeSave = useCallback(() => {
    reset();
    setViewModeSignal((value = 0) => value + 1);
  }, [reset]);

  const deletedRows = useMemo(
    () => serverRows.filter((row) => deletedRowIds.has(row.clientId)),
    [deletedRowIds, serverRows],
  );
  const hasChanges =
    isEditing && (deletedRowIds.size > 0 || !areRowsEqual(rows, serverRows));
  const rowErrorVisibility = useMemo<RowErrorVisibility>(
    () => ({
      saveAttemptedRowIds,
      touchedFields: new Map(
        [...fieldStates].map(([clientId, state]) => [clientId, state.touched]),
      ),
    }),
    [fieldStates, saveAttemptedRowIds],
  );

  return {
    addRow,
    applySaveValidation,
    cancelEditing: reset,
    changeRow,
    clearValidationErrors: () => setValidationErrors(new Map()),
    completeSave,
    deleteRow,
    deletedRows,
    editingRowIds,
    enterEditing,
    hasChanges,
    isEditing,
    openRows,
    patchRow,
    rowErrorVisibility,
    rows,
    setEditingRowIds,
    setRows,
    touchField,
    validationErrors,
    viewModeSignal,
  };
}

// Rows are validated while they are edited, like regular form fields.
// - A saved row shows all its errors at once.
// - A new row shows only the errors of its touched fields, so the fields the
//   user has not reached yet stay quiet. After a save attempt it shows all
//   of them.
// - A new row in its initial state - untouched, or edited back to it - is
//   the unused "add a row" slot and is never validated.
// An error is keyed by the field it is shown on.
export function getLiveRowErrors<T extends EditableRow, E extends object>(
  rows: T[],
  savedErrors: Map<string, E>,
  validateRow: (row: T) => E,
  isInitialNewRow: (row: T) => boolean,
  { saveAttemptedRowIds, touchedFields }: RowErrorVisibility,
) {
  const errors = new Map(savedErrors);
  rows.forEach((row) => {
    if (isInitialNewRow(row)) {
      errors.delete(row.clientId);
      return;
    }
    // Keeps cross-row errors (e.g. duplicates) found by the last save.
    const rowErrors = {
      ...savedErrors.get(row.clientId),
      ...validateRow(row),
    } as E;
    if (row.id > 0 || saveAttemptedRowIds.has(row.clientId)) {
      errors.set(row.clientId, rowErrors);
      return;
    }
    const touched = touchedFields.get(row.clientId);
    errors.set(
      row.clientId,
      Object.fromEntries(
        Object.entries(rowErrors).filter(([field]) => touched?.has(field)),
      ) as E,
    );
  });
  return errors;
}

export function useLiveRowErrors<T extends EditableRow, E extends object>(
  rows: T[],
  savedErrors: Map<string, E>,
  validateRow: (row: T) => E,
  isInitialNewRow: (row: T) => boolean,
  visibility: RowErrorVisibility,
) {
  return useMemo(
    () =>
      getLiveRowErrors(
        rows,
        savedErrors,
        validateRow,
        isInitialNewRow,
        visibility,
      ),
    [isInitialNewRow, rows, savedErrors, validateRow, visibility],
  );
}

type CreateEditableRowDeltaOptions<T extends EditableRow, C, U, D> = {
  deletedRows: T[];
  includeMissingServerRowsAsDeleted?: boolean;
  isSameRow: (working: T, server: T) => boolean;
  serverRows: T[];
  toCreated: (row: T) => C;
  toDeleted: (row: T) => D;
  toUpdated: (row: T) => U;
  workingRows: T[];
};

export function createEditableRowDelta<T extends EditableRow, C, U, D>({
  deletedRows,
  includeMissingServerRowsAsDeleted = false,
  isSameRow,
  serverRows,
  toCreated,
  toDeleted,
  toUpdated,
  workingRows,
}: CreateEditableRowDeltaOptions<T, C, U, D>) {
  const serverById = new Map(serverRows.map((row) => [row.id, row]));
  const workingIds = new Set(
    workingRows.filter((row) => row.id > 0).map((row) => row.id),
  );
  const deletedById = new Map(deletedRows.map((row) => [row.id, row]));
  if (includeMissingServerRowsAsDeleted) {
    serverRows.forEach((row) => {
      if (!workingIds.has(row.id)) {
        deletedById.set(row.id, row);
      }
    });
  }
  const created = workingRows.filter((row) => row.id < 0).map(toCreated);
  const updated = workingRows
    .filter((row) => {
      const serverRow = serverById.get(row.id);
      return row.id > 0 && serverRow && !isSameRow(row, serverRow);
    })
    .map(toUpdated);
  return {
    created,
    updated,
    deleted: [...deletedById.values()].map(toDeleted),
  };
}
